import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const budgets = ["< $5k", "$5k–$15k", "$15k–$30k", "$30k+", "Retainer"];

const initialState = {
  name: "",
  email: "",
  company: "",
  budget: "",
  message: "",
};

function FieldLabel({ label }) {
  return (
    <div className="border-b border-[#FFFFFF]/30 [&:nth-child(2)]:border-l px-4 md:px-5 py-3 label-mono text-[#FFFFFF]/60">
      {label}
    </div>
  );
}

function Field({ name, value, onChange, placeholder, type = "text", testid, required }) {
  return (
    <input
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      data-testid={testid}
      className="bg-transparent text-[#FFFFFF] placeholder:text-[#FFFFFF]/30 px-4 md:px-5 py-4 outline-none font-sans-ibm border-t border-[#FFFFFF]/30 [&:nth-of-type(even)]:border-l focus:border-[#4C1D95]"
    />
  );
}

function getErrorMessage(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(err?.response?.data)) {
    return err.response.data[0]?.msg || "Submission failed.";
  }
  return "Submission failed. Try again or email parastousaba@gmail.com";
}

export default function ContactForm() {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const isValid = () =>
    form.name.trim() && form.email.trim() && form.message.trim().length >= 10;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!isValid()) {
      toast.error("Please complete name, email and a message (10+ chars).");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        budget: form.budget || null,
        message: form.message.trim(),
      });
      toast.success("Brief received. Reply within 24h.");
      setSubmitted(true);
      setForm(initialState);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="col-span-12 lg:col-span-7">
      <form
        onSubmit={handleSubmit}
        className="border border-[#FFFFFF]/30 bg-[#0A0A0A] p-6 md:p-10"
        data-testid="contact-form"
      >
        <div className="grid grid-cols-2 gap-0 border border-[#FFFFFF]/30">
          <FieldLabel label="01 / Name" />
          <FieldLabel label="02 / Email" />
          <Field
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Jane Doe"
            testid="contact-input-name"
            required
          />
          <Field
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            placeholder="jane@company.com"
            testid="contact-input-email"
            required
          />
          <FieldLabel label="03 / Company" />
          <FieldLabel label="04 / Budget" />
          <Field
            name="company"
            value={form.company}
            onChange={handleChange}
            placeholder="Acme Inc."
            testid="contact-input-company"
          />
          <div className="border-t border-l border-[#FFFFFF]/30 px-4 md:px-5 py-4">
            <select
              name="budget"
              value={form.budget}
              onChange={handleChange}
              className="w-full bg-transparent text-[#FFFFFF] label-mono outline-none cursor-pointer"
              data-testid="contact-input-budget"
            >
              <option value="" className="bg-[#0A0A0A]">
                Select range
              </option>
              {budgets.map((b) => (
                <option key={b} value={b} className="bg-[#0A0A0A]">
                  {b}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-0 border-x border-b border-[#FFFFFF]/30">
          <div className="border-b border-[#FFFFFF]/30 px-4 md:px-5 py-3 label-mono text-[#FFFFFF]/60">
            05 / Your project
          </div>
          <textarea
            name="message"
            value={form.message}
            onChange={handleChange}
            rows={6}
            placeholder="What are you building or trying to fix? Share the product, the users, and where things feel tangled."
            className="w-full bg-transparent text-[#FFFFFF] placeholder:text-[#FFFFFF]/30 px-4 md:px-5 py-4 outline-none resize-none font-sans-ibm"
            data-testid="contact-input-message"
            required
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 mt-6">
          <p className="label-mono text-[#FFFFFF]/50 max-w-md">
            By submitting, you agree the brief may be stored to respond. No
            newsletters. Ever.
          </p>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-3 bg-[#4C1D95] text-[#FFFFFF] px-8 py-4 label-mono border border-[#4C1D95] hover:bg-[#FFFFFF] hover:text-[#0A0A0A] hover:border-[#FFFFFF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="contact-submit-button"
          >
            {loading ? "Sending…" : submitted ? "Send another" : "Send message"}
            <ArrowUpRight size={16} strokeWidth={1.5} />
          </button>
        </div>
      </form>
    </div>
  );
}
